import { useSyncExternalStore } from "react";

import { CommMediaApiError, importeerDocument, type DocumentVoorstel } from "../commMediaApi";

// communicatie: het overnemen van een document als een taak die los van het venster doorloopt.
//
// WAAROM. Een hele pagina laten nabouwen duurt al snel een paar minuten. In een venster met alleen
// "Bezig met lezen…" klikken mensen dan weg — en daarmee was het werk weg. Nu hoort de taak bij de
// app en niet bij het venster: je sluit het venster, werkt verder, en rechtsonder zie je hoe lang hij
// al bezig is en wanneer hij klaar is. Met "Bekijken" kom je terug bij het sjabloon, met het resultaat
// klaar om over te nemen.
//
// Eén taak tegelijk, en alleen in dit tabblad: het resultaat leeft in het geheugen van de app. Een
// herlaad van de pagina gooit hem weg; daar hoort een melding bij, geen stil verlies (zie de melding).

export interface ImportBron {
  /** Het sjabloon waarvoor het document wordt nagebouwd; daar brengt "Bekijken" je naartoe. */
  templateId: string;
  templateNaam: string;
}

interface Basis extends ImportBron {
  bestandsnaam: string;
}

export type ImportTaak =
  | { status: "leeg" }
  | (Basis & { status: "bezig"; gestart: number })
  | (Basis & { status: "klaar"; voorstel: DocumentVoorstel; bekijken: boolean })
  /** {@code code}: de http-status als de api weigerde (401 = sessie verlopen), anders null. */
  /** {@code diagnose}: wat de server onderweg zag -- staat alleen bij een fout, om door te geven. */
  | (Basis & {
      status: "fout"; melding: string | null; code: number | null; bekijken: boolean;
      diagnose: string | null;
    });

let taak: ImportTaak = { status: "leeg" };
let dialoogOpen = false;
// Elke start krijgt een nummer. Komt er een antwoord binnen van een taak die intussen is gestopt of
// vervangen, dan hoort dat niemand meer.
let volgnummer = 0;
const luisteraars = new Set<() => void>();

function zet(nieuw: ImportTaak) {
  taak = nieuw;
  for (const l of luisteraars) l();
}

export const importTaak = {
  get: (): ImportTaak => taak,
  subscribe(l: () => void) {
    luisteraars.add(l);
    return () => {
      luisteraars.delete(l);
    };
  },

  /** Start het nabouwen. Een lopende of afgeronde taak wordt vervangen. */
  async start(bestand: File, bron: ImportBron): Promise<void> {
    const mijn = ++volgnummer;
    const basis: Basis = { ...bron, bestandsnaam: bestand.name };
    zet({ ...basis, status: "bezig", gestart: Date.now() });
    try {
      const uit = await importeerDocument(bestand);
      if (mijn !== volgnummer) return;
      if (!uit.beschikbaar || !uit.bodyHtml) {
        zet({ ...basis, status: "fout", melding: uit.melding ?? null, code: null, bekijken: false,
          diagnose: uit.diagnose ?? null });
      } else {
        zet({ ...basis, status: "klaar", voorstel: uit, bekijken: false });
      }
    } catch (e) {
      // Na een herstart van de api hoort je sessie bij de oude: dan weigert hij met 401. Dat moet je
      // horen, anders lijkt het document de schuldige ("kon niet worden gelezen").
      const code = e instanceof CommMediaApiError ? e.statusCode : null;
      if (mijn === volgnummer) {
        zet({ ...basis, status: "fout", melding: null, code, bekijken: false, diagnose: null });
      }
    }
  },

  /** Stoppen of weggooien: een antwoord dat hierna nog binnenkomt, wordt genegeerd. */
  stop() {
    volgnummer++;
    zet({ status: "leeg" });
  },

  /** "Bekijken" in de melding: het sjabloon openen en daar het resultaat tonen. */
  vraagBekijken() {
    if (taak.status === "klaar" || taak.status === "fout") zet({ ...taak, bekijken: true });
  },

  /** Het sjabloon heeft het resultaat laten zien; de vraag is afgehandeld. */
  bekeken() {
    if ((taak.status === "klaar" || taak.status === "fout") && taak.bekijken) {
      zet({ ...taak, bekijken: false });
    }
  },

  /** Staat het venster open, dan is een melding rechtsonder dubbelop. */
  isDialoogOpen: () => dialoogOpen,
  setDialoogOpen(open: boolean) {
    if (dialoogOpen === open) return;
    dialoogOpen = open;
    for (const l of luisteraars) l();
  },
};

export function useImportTaak(): ImportTaak {
  return useSyncExternalStore(importTaak.subscribe, importTaak.get, importTaak.get);
}

export function useImportDialoogOpen(): boolean {
  return useSyncExternalStore(importTaak.subscribe, importTaak.isDialoogOpen, importTaak.isDialoogOpen);
}

/** Hoe lang de taak al loopt, als 0:42 of 2:05. */
export function verstreken(gestart: number, nu: number): string {
  const s = Math.max(0, Math.floor((nu - gestart) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
